import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck, ShieldOff } from 'lucide-react';
import { listAccessReviewAccounts } from '@/api/accessReviews';
import { formatDateTime } from '@/lib/dateFormat';
import { useSort, sortRows } from '@/lib/sortTable';
import { SortHeader } from '@/components/ui/SortHeader';

type StatusFilter = 'all' | 'enabled' | 'disabled';

/**
 * System Access Reviews (SAR) — read-only access-landscape view. Lists every
 * account (Person + system) from oscar.vAccount with its Account Type, owner (a
 * person's manager / a system account's owner), enabled state and last sign-in /
 * password change, so the team can see the access picture. The review campaigns
 * / attestation workflow are a later phase.
 */
export function AccessReviews() {
  const [q, setQ] = useState('');
  const [type, setType] = useState<string>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const { sortKey, sortDir, onSort } = useSort('account');

  const query = useQuery({ queryKey: ['sar-accounts'], queryFn: ({ signal }) => listAccessReviewAccounts(signal) });
  const accounts = useMemo(() => query.data ?? [], [query.data]);

  // Distinct account types present, for the filter dropdown.
  const types = useMemo(() => {
    const seen = new Map<string, string>();
    for (const a of accounts) {
      if (a.AccountType) seen.set(a.AccountType, a.AccountTypeName ?? a.AccountType);
    }
    return [...seen.entries()].sort((x, y) => x[1].localeCompare(y[1]));
  }, [accounts]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return accounts.filter((a) => {
      if (status === 'enabled' && !a.IsEnabled) return false;
      if (status === 'disabled' && a.IsEnabled) return false;
      if (type === '__none') { if (a.AccountType) return false; }
      else if (type !== 'all' && a.AccountType !== type) return false;
      if (term && !`${a.DisplayName} ${a.UPN ?? ''} ${a.OwnerName ?? ''}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [accounts, q, type, status]);

  const sorted = useMemo(() => sortRows(filtered, (a) => {
    switch (sortKey) {
      case 'type': return a.AccountTypeName ?? a.AccountType;
      case 'owner': return a.OwnerName;
      case 'status': return a.IsEnabled;
      case 'lastSignIn': return a.LastSignIn;
      case 'lastPwd': return a.LastPasswordChange;
      case 'deptBranch': return a.Department ?? a.BranchCode;
      default: return a.DisplayName;
    }
  }, sortDir), [filtered, sortKey, sortDir]);

  const enabledCount = accounts.filter((a) => a.IsEnabled).length;
  const noOwner = accounts.filter((a) => !a.OwnerName).length;

  return (
    <section>
      <header className="mb-5">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-navy">
          <ClipboardCheck className="h-6 w-6 text-navy" aria-hidden /> System Access Reviews
        </h1>
        <p className="mt-1 text-sm text-darkGrey/80">
          The current access landscape — every account, its Account Type, owner, and activity.
          Review campaigns (owner recertification) are a later phase.
        </p>
      </header>

      {query.isLoading && <p className="text-sm text-darkGrey/60">Loading…</p>}
      {query.isError && <p className="text-sm text-red">Could not load the accounts.</p>}

      {!query.isLoading && !query.isError && (
        <>
          <div className="mb-4 flex flex-wrap gap-3 text-sm">
            <Stat label="Accounts" value={accounts.length} />
            <Stat label="Enabled" value={enabledCount} />
            <Stat label="Disabled" value={accounts.length - enabledCount} />
            <Stat label="No owner" value={noOwner} tone={noOwner > 0 ? 'warn' : 'default'} />
          </div>

          <div className="mb-3 flex flex-wrap items-end gap-2">
            <label className="flex flex-col text-xs font-semibold text-darkGrey">
              Search
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, UPN or owner" aria-label="Search accounts" className={inputCls} />
            </label>
            <label className="flex flex-col text-xs font-semibold text-darkGrey">
              Account type
              <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Filter by account type" className={inputCls}>
                <option value="all">All types</option>
                {types.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                {accounts.some((a) => !a.AccountType) && <option value="__none">Uncategorised</option>}
              </select>
            </label>
            <label className="flex flex-col text-xs font-semibold text-darkGrey">
              Status
              <select value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)} aria-label="Filter by status" className={inputCls}>
                <option value="all">All</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </label>
          </div>

          <div className="overflow-x-auto rounded border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-bg text-left text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
                <tr>
                  <SortHeader label="Account" columnKey="account" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortHeader label="Type" columnKey="type" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortHeader label="Owner" columnKey="owner" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortHeader label="Status" columnKey="status" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortHeader label="Last sign-in" columnKey="lastSignIn" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortHeader label="Last password change" columnKey="lastPwd" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortHeader label="Dept / Branch" columnKey="deptBranch" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-white">
                {sorted.map((a) => (
                  <tr key={`${a.AccountKind}-${a.AccountId}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-navy">{a.DisplayName}</div>
                      <div className="text-xs text-darkGrey/60">{a.UPN ?? '—'}</div>
                    </td>
                    <td className="px-3 py-2 text-darkGrey/80">{a.AccountTypeName ?? a.AccountType ?? <span className="text-darkGrey/50">Uncategorised</span>}</td>
                    <td className="px-3 py-2">
                      {a.OwnerName ?? <span className="text-orange">No owner</span>}
                    </td>
                    <td className="px-3 py-2">
                      {a.IsEnabled
                        ? <span className="text-green">Enabled</span>
                        : <span className="inline-flex items-center gap-1 text-red"><ShieldOff className="h-3.5 w-3.5" aria-hidden /> Disabled</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-darkGrey/80">{a.LastSignIn ? formatDateTime(a.LastSignIn) : '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-darkGrey/80">{a.LastPasswordChange ? formatDateTime(a.LastPasswordChange) : '—'}</td>
                    <td className="px-3 py-2 text-darkGrey/80">{a.Department ?? a.BranchCode ?? '—'}</td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-darkGrey/60">No accounts match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

const inputCls = 'mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal';

function Stat({ label, value, tone = 'default' }: { label: string; value: number; tone?: 'default' | 'warn' }) {
  return (
    <div className={`rounded border px-3 py-2 ${tone === 'warn' ? 'border-orange/40 bg-orange/5' : 'border-border bg-bg/40'}`}>
      <div className={`text-lg font-bold ${tone === 'warn' ? 'text-orange' : 'text-navy'}`}>{value}</div>
      <div className="text-xs text-darkGrey/70">{label}</div>
    </div>
  );
}
